import { useCallback, useEffect, useMemo, useState } from 'react'
import type { BoggleRunSummaryScore } from '~/boggle/config/runSummaryScore'
import {
  RUN_RESULTS_SCORE_RAMP_BASE_MS,
  scaleRunResultsScoreRampDurationMs,
} from '~/boggle/ui/run/runResultsScoreRamp'
import { RUN_SUMMARY_METRIC_RAMP_MS } from '~/boggle/ui/run/RunSummary'
import { resolveRampingNumberDurationMs } from '~/boggle/ui/atoms/RampingNumberText'
import type { RunSummaryAchievementEvent } from '~/boggle/lib/achievements/runSummaryAchievementEvents'

/** Discriminator for which anchor a tag attaches to; resolved by `RunSurface`. */
export type RunResultsTagAnchor =
  | { kind: 'badge'; index: number }
  | { kind: 'tier'; index: number }

export type RunResultsActiveTag = {
  anchor: RunResultsTagAnchor
  durationMs: number
  preDrainDelayMs: number
  /** Forces a remount when the same anchor is used twice in a row (rare; safety). */
  key: string
  startValue: number
}

/** Pooled badge "+N" tag: one portal, jumping cumulative value, final drain after all badges. */
export type RunResultsBadgeScoreTag = {
  anchor: RunResultsTagAnchor
  cumulativeValue: number
  finalDrain: null | {
    durationMs: number
    key: string
    preDrainDelayMs: number
  }
  jiggleNonce: number
}

/** Signal for the Gold tracker's internal drain animation; bumped per sequence. */
export type RunResultsGoldDrain = {
  key: number
  preDelayMs: number
  drainDurationMs: number
}

/**
 * Per-badge presentation phase. Threaded into `RunSummary`'s reveal stage:
 *   - `pending`: not yet revealed (badge hidden everywhere)
 *   - `revealing`: 80px medallion shown at the bottom; floating tag fires from it
 *   - `flying`: medallion is moving from the bottom into the centered list
 *     (FLIP-style; the next badge may already be revealing in parallel)
 *   - `settled`: medallion lives in the centered list at 40px
 */
export type BoggleBadgeRevealPhase = 'pending' | 'revealing' | 'flying' | 'settled'

/** Per-step timings; tuned for a tight sequence with arcade-style ramp-ups. */
const TAG_DRAIN_MS = RUN_RESULTS_SCORE_RAMP_BASE_MS
const TAG_FADE_OUT_MS = 180
/** Hold at full value after appearing before the drain starts. */
const TAG_PRE_DRAIN_DELAY_MS = 480
/** Gap after a tag finishes before the next step kicks off. */
const TAG_GAP_MS = 180
const TIER_REVEAL_STAGGER_EXTRA_WHEN_TAG_MS = 200
const TIER_REVEAL_OVERLAP_BEFORE_PREVIOUS_RAMP_END_MS = 300
const TIER_REVEAL_MIN_STAGGER_MS = 400
const BADGE_POOLED_TAG_PRE_DRAIN_DELAY_MS = 200
/** After the tier metric finishes ramping, wait this long before mounting the +points tag. */
const TIER_TAG_AFTER_METRIC_MS = 240
/**
 * Lead time between the last tier meter filling and the first badge reveal.
 * Mirrors tier stagger cadence plus an extra pause so tiers can finish visually;
 * {@link BADGE_SEQUENCE_OVERLAP_TIER_MS} pulls the badge beat earlier to overlap late tier motion.
 */
const BADGE_SEQUENCE_OVERLAP_TIER_MS = 700
const BADGE_SEQUENCE_LEAD_MS =
  TIER_REVEAL_STAGGER_EXTRA_WHEN_TAG_MS + 700 + 400 - BADGE_SEQUENCE_OVERLAP_TIER_MS
/** Pop + spring duration before the per-badge score tag fires from the badge. */
const BADGE_REVEAL_POP_MS = 180
/**
 * Hold between the badge score tag appearing and the count-down (drain) starting.
 * Mirrors {@link TAG_PRE_DRAIN_DELAY_MS} so badge tags hold at full value for
 * the same beat as tier tags before counting down.
 */
const BADGE_TAG_PRE_DRAIN_DELAY_MS = TAG_PRE_DRAIN_DELAY_MS
/**
 * Duration of the FLIP move from the bottom 80px medallion into the centered
 * 40px list slot. Must match `BoggleBadgeRevealStage` so the visual timing and
 * the choreography state machine agree.
 */
export const BOGGLE_BADGE_FLY_DURATION_MS = 200
/**
 * The Gold tracker plays a 200ms scale-up "puff" before its 300ms hold, then drains.
 * Mirrors `SCALE_UP_MS` inside `GoldTilesRunTracker` — score ramp must wait the full
 * pre-drain (scale-up + hold) so it kicks off exactly when the tracker starts draining.
 */
const GOLD_SCALE_UP_MS = 200
const GOLD_HOLD_MS = 300
const GOLD_DRAIN_MS = RUN_RESULTS_SCORE_RAMP_BASE_MS
/**
 * Begin tier meter/tag choreography this many ms before the gold count finishes
 * draining (i.e. when ~500ms of drain motion remains).
 */
const TIER_REVEAL_LEAD_BEFORE_GOLD_DRAIN_END_MS = 300
/** Wait for the play→results crossfade to settle before the sequence kicks off. */
const INITIAL_DELAY_MS = 720
/** After pooled tag value hits zero; matches hold in `FloatingAccumulatingScoreTag` before fade-out. */
const BADGE_FLOATING_TAG_POST_DRAIN_HOLD_MS = 80
const OUTCOME_DELAY_MS = 420

/** Delay after the rating beat before Play Again / trophy reveal; toasts are parallel, not gated on this. */
export const RUN_SUMMARY_ACTIONS_REVEAL_DELAY_MS = 300
const RUN_SUMMARY_ACHIEVEMENT_TOAST_MS = 1900
const RUN_SUMMARY_ACHIEVEMENT_TOAST_GAP_MS = 180

/** Total time from a badge entering `revealing` until the next badge enters `revealing`. */
const BADGE_REVEAL_TO_FLY_MS = BADGE_REVEAL_POP_MS + BADGE_TAG_PRE_DRAIN_DELAY_MS

/**
 * Pure helper exported for tests — builds the per-badge reveal schedule used
 * inside the effect. The FLIP starts after each reveal’s tag spawn + hold; the
 * main Score stays flat until the final pooled-tag drain (not on these beats).
 */
export type BoggleBadgeRevealStep = {
  /** Entering `revealing` phase (pop + spring at the bottom). */
  revealAt: number
  /** When the per-badge floating tag mounts. */
  tagSpawnAt: number
  /** When the FLIP begins; last beat before the final pooled-tag drain kicks off. */
  scoreBumpAt: number
  /** Entering `flying` phase (FLIP move starts; next badge begins revealing here too). */
  flyStartAt: number
  /** Entering `settled` phase (FLIP move complete). */
  settleAt: number
}

export function planBoggleBadgeRevealSchedule({
  badgeCount,
  startAt,
}: {
  badgeCount: number
  startAt: number
}): BoggleBadgeRevealStep[] {
  const steps: BoggleBadgeRevealStep[] = []
  let revealAt = startAt
  for (let i = 0; i < badgeCount; i++) {
    const tagSpawnAt = revealAt + BADGE_REVEAL_POP_MS
    const scoreBumpAt = tagSpawnAt + BADGE_TAG_PRE_DRAIN_DELAY_MS
    /** FLIP begins after the per-badge hold; main Score does not step on this beat. */
    const flyStartAt = scoreBumpAt
    const settleAt = flyStartAt + BOGGLE_BADGE_FLY_DURATION_MS
    steps.push({ flyStartAt, revealAt, scoreBumpAt, settleAt, tagSpawnAt })
    revealAt = flyStartAt
  }
  return steps
}

/**
 * Drives the end-of-run scoring choreography: which floating tag is active, the
 * Gold-tracker drain signal, how many tier lines have been revealed, the running
 * Score total, the per-badge reveal phase, and when the Rating + outcome copy
 * fade in.
 *
 * When the run scored gold points, tier row reveals begin during the last ~500ms
 * of the gold counter drain so the tier meters start while gold is still in motion.
 *
 * Tier steps are spawned sequentially; the running total ramps up by an equal
 * amount each step, synchronized via matched durations and the shared pre-drain
 * delay. Tier rows reveal + ramp their metric (duration matches `RampingNumberText`
 * scaling), then a transfer tag flies on a staggered schedule.
 *
 * Badge steps follow tier steps. Tier floating tags and the single pooled badge
 * tag can overlap (separate portals). Each badge plays a four-phase sequence:
 *   1. `revealing` — 80px medallion pops in at the bottom
 *   2. the pooled "+N" tag jumps to the new cumulative badge sum (jiggle); main Score stays flat until that final pooled drain starts with the last fly-in
 *   3. `flying` — FLIP move into the centered BadgeList (next badge may begin revealing here)
 *   4. `settled` — slot becomes a stable list medallion
 *
 * After the last badge begins flying into the list, the pooled tag drains immediately while the Score ramps in lockstep.
 */
function settleAllBadgeRevealPhases(
  badgeLines: BoggleRunSummaryScore['badgeLines'],
): BoggleBadgeRevealPhase[] {
  return Array.from(
    { length: badgeLines.length },
    (): BoggleBadgeRevealPhase => 'settled',
  )
}

type SummaryChoreographyStep =
  | { kind: 'gold' }
  | { kind: 'tier'; index: number }
  | { kind: 'badges' }

export function buildSummaryChoreographySteps(
  summaryScore: BoggleRunSummaryScore,
): SummaryChoreographyStep[] {
  const steps: SummaryChoreographyStep[] = []
  const goldLine = summaryScore.coreLines.find(line => line.id === 'gold-tiles-used')
  if (goldLine && goldLine.points > 0) {
    steps.push({ kind: 'gold' })
  }
  summaryScore.tierLines.forEach((_, index) => {
    steps.push({ kind: 'tier', index })
  })
  if (summaryScore.badgeLines.length > 0) {
    steps.push({ kind: 'badges' })
  }
  return steps
}

export function useRunResultsChoreography({
  achievementEvents,
  isActive,
  summaryScore,
}: {
  achievementEvents?: readonly RunSummaryAchievementEvent[] | null
  isActive: boolean
  summaryScore: BoggleRunSummaryScore
}) {
  /**
   * Tiles Cleared is *already* the displayed Score at phase change (same DOM counter, just relabeled),
   * so its points seed the running total. Subsequent steps (gold drain, tier tags) ramp on top of it.
   */
  const tilesSeedPoints =
    summaryScore.coreLines.find(line => line.id === 'tiles-cleared')?.points ?? 0
  const [score, setScore] = useState(tilesSeedPoints)
  const [tierActiveTag, setTierActiveTag] = useState<RunResultsActiveTag | null>(null)
  const [badgeScoreTag, setBadgeScoreTag] = useState<RunResultsBadgeScoreTag | null>(null)
  const [goldDrain, setGoldDrain] = useState<RunResultsGoldDrain | null>(null)
  const [revealedTierCount, setRevealedTierCount] = useState(0)
  const [badgeRevealPhases, setBadgeRevealPhases] = useState<BoggleBadgeRevealPhase[]>(
    () => Array.from({ length: summaryScore.badgeLines.length }, () => 'pending'),
  )
  const [showRating, setShowRating] = useState(false)
  const [showOutcomeCopy, setShowOutcomeCopy] = useState(false)
  const [skippedToSummaryEnd, setSkippedToSummaryEnd] = useState(false)
  const [completedThroughStep, setCompletedThroughStep] = useState(-1)
  const [resultsScoringAnimationsComplete, setResultsScoringAnimationsComplete] =
    useState(false)
  const [summaryActionsRevealReady, setSummaryActionsRevealReady] = useState(false)
  const [activeAchievementEvent, setActiveAchievementEvent] =
    useState<RunSummaryAchievementEvent | null>(null)

  const choreographySteps = useMemo(
    () => buildSummaryChoreographySteps(summaryScore),
    [summaryScore],
  )

  /** Stable cache key — restart the sequence whenever any contributing value changes. */
  const sequenceKey = useMemo(
    () =>
      [
        summaryScore.total,
        summaryScore.rating.id,
        ...summaryScore.coreLines.map(line => `${line.id}:${line.points}`),
        ...summaryScore.tierLines.map(
          line => `${line.category.id}:${line.metricValue}:${line.points}`,
        ),
        ...summaryScore.badgeLines.map(line => `${line.badge.id}:${line.points}`),
      ].join('|'),
    [summaryScore],
  )

  useEffect(() => {
    if (!isActive) {
      setCompletedThroughStep(-1)
    }
  }, [isActive, sequenceKey])

  useEffect(() => {
    const badgeCount = summaryScore.badgeLines.length
    const initialPhases: BoggleBadgeRevealPhase[] = Array.from(
      { length: badgeCount },
      () => 'pending',
    )

    if (!isActive) {
      // Hold at the seeded total while the results body is offscreen / faded out.
      setScore(tilesSeedPoints)
      setTierActiveTag(null)
      setBadgeScoreTag(null)
      setGoldDrain(null)
      setRevealedTierCount(0)
      setBadgeRevealPhases(initialPhases)
      setShowRating(false)
      setShowOutcomeCopy(false)
      setSkippedToSummaryEnd(false)
      setCompletedThroughStep(-1)
      setResultsScoringAnimationsComplete(false)
      setSummaryActionsRevealReady(false)
      setActiveAchievementEvent(null)
      return
    }

    if (skippedToSummaryEnd) {
      setScore(summaryScore.total)
      setTierActiveTag(null)
      setBadgeScoreTag(null)
      setGoldDrain(null)
      setRevealedTierCount(summaryScore.tierLines.length)
      setBadgeRevealPhases(settleAllBadgeRevealPhases(summaryScore.badgeLines))
      setShowRating(true)
      setShowOutcomeCopy(true)
      setResultsScoringAnimationsComplete(true)
      setSummaryActionsRevealReady(true)
      setActiveAchievementEvent(null)
      return
    }

    if (completedThroughStep < 0) {
      setScore(tilesSeedPoints)
      setTierActiveTag(null)
      setBadgeScoreTag(null)
      setGoldDrain(null)
      setRevealedTierCount(0)
      setBadgeRevealPhases(initialPhases)
      setShowRating(false)
      setShowOutcomeCopy(false)
      setResultsScoringAnimationsComplete(false)
      setSummaryActionsRevealReady(false)
      setActiveAchievementEvent(null)
    }

    const timers: number[] = []
    const goldLine = summaryScore.coreLines.find(line => line.id === 'gold-tiles-used')
    const steps = choreographySteps

    let cumulativeScore = tilesSeedPoints
    let cumulativeBadgePoints = 0
    let revealedTiers = 0
    let firstTierRevealAt = completedThroughStep >= 0 ? 0 : INITIAL_DELAY_MS

    const setBadgePhaseAt = (index: number, phase: BoggleBadgeRevealPhase) => {
      setBadgeRevealPhases(prev => {
        if (prev[index] === phase) {
          return prev
        }
        const next = prev.slice()
        next[index] = phase
        return next
      })
    }

    const snapTierStep = (index: number) => {
      const line = summaryScore.tierLines[index]
      if (!line) {
        return
      }
      revealedTiers = index + 1
      if (line.points > 0) {
        cumulativeScore += line.points
        const tierDrainMs = scaleRunResultsScoreRampDurationMs(line.points, TAG_DRAIN_MS)
        setTierActiveTag({
          anchor: { kind: 'tier', index },
          durationMs: tierDrainMs,
          key: `${sequenceKey}:tier-${index}-snap`,
          preDrainDelayMs: 0,
          startValue: line.points,
        })
      }
    }

    for (
      let stepIndex = 0;
      stepIndex <= completedThroughStep && stepIndex < steps.length;
      stepIndex++
    ) {
      const step = steps[stepIndex]!
      if (step.kind === 'gold' && goldLine) {
        cumulativeScore += goldLine.points
        setGoldDrain(null)
        firstTierRevealAt = 0
      } else if (step.kind === 'tier') {
        snapTierStep(step.index)
      } else if (step.kind === 'badges') {
        cumulativeBadgePoints = summaryScore.badgeLines.reduce(
          (sum, line) => sum + line.points,
          0,
        )
        cumulativeScore += cumulativeBadgePoints
        setBadgeRevealPhases(settleAllBadgeRevealPhases(summaryScore.badgeLines))
        setBadgeScoreTag(null)
        setTierActiveTag(null)
      }
    }

    if (completedThroughStep >= 0) {
      setScore(cumulativeScore)
      setRevealedTierCount(revealedTiers)
    }

    let scheduleStepIndex = 0

    let t = completedThroughStep >= 0 ? 0 : INITIAL_DELAY_MS

    /**
     * Tier tag step: spawn the floating "+N" tag, then (after `TAG_PRE_DRAIN_DELAY_MS`) bump
     * the score so its `RampingNumberText` ramps in lockstep with the tag's drain.
     * Returns ms timestamp when this tag step is fully finished (fade + gap), or `scheduleAt`
     * when `value <= 0`.
     */
    const enqueueTierTagAt = (
      scheduleAt: number,
      anchor: RunResultsTagAnchor,
      value: number,
      label: string,
    ): number => {
      if (value <= 0) {
        return scheduleAt
      }

      cumulativeScore += value
      const targetScore = cumulativeScore
      const tagKey = `${sequenceKey}:${label}`
      const tierDrainMs = scaleRunResultsScoreRampDurationMs(value, TAG_DRAIN_MS)

      timers.push(
        window.setTimeout(() => {
          setTierActiveTag({
            anchor,
            durationMs: tierDrainMs,
            key: tagKey,
            preDrainDelayMs: TAG_PRE_DRAIN_DELAY_MS,
            startValue: value,
          })
        }, scheduleAt),
      )

      timers.push(
        window.setTimeout(() => {
          setScore(targetScore)
        }, scheduleAt + TAG_PRE_DRAIN_DELAY_MS),
      )

      return (
        scheduleAt + TAG_PRE_DRAIN_DELAY_MS + tierDrainMs + TAG_FADE_OUT_MS + TAG_GAP_MS
      )
    }

    /**
     * Gold step: signal the Gold tracker to boost-and-drain, ramp the score in sync once
     * its pre-delay completes, then move on.
     */
    if (goldLine && goldLine.points > 0) {
      const goldScheduleStepIndex = scheduleStepIndex
      scheduleStepIndex += 1
      if (goldScheduleStepIndex > completedThroughStep) {
        const scheduleAt = t
        cumulativeScore += goldLine.points
        const targetScore = cumulativeScore
        const goldDrainMs = scaleRunResultsScoreRampDurationMs(
          goldLine.points,
          GOLD_DRAIN_MS,
        )

        timers.push(
          window.setTimeout(() => {
            setGoldDrain({
              key: scheduleAt,
              preDelayMs: GOLD_HOLD_MS,
              drainDurationMs: goldDrainMs,
            })
          }, scheduleAt),
        )

        timers.push(
          window.setTimeout(
            () => {
              setScore(targetScore)
            },
            scheduleAt + GOLD_SCALE_UP_MS + GOLD_HOLD_MS,
          ),
        )

        t += GOLD_SCALE_UP_MS + GOLD_HOLD_MS + goldDrainMs + TAG_GAP_MS
        firstTierRevealAt =
          scheduleAt +
          GOLD_SCALE_UP_MS +
          GOLD_HOLD_MS +
          Math.max(0, goldDrainMs - TIER_REVEAL_LEAD_BEFORE_GOLD_DRAIN_END_MS)
      }
    }

    let nextTierRevealAt = firstTierRevealAt
    /** Next time a tier tag may start; keeps one tag on screen even when reveals overlap. */
    let nextTagSlotAt = firstTierRevealAt
    let choreoEnd = t
    /**
     * Latest moment the last tier meter has finished filling. Badges chase
     * this rather than `choreoEnd` so the badge sequence starts the moment
     * the meters are full — the cadence-matching gap is added separately.
     */
    let lastMeterFillAt = firstTierRevealAt

    summaryScore.tierLines.forEach((line, index) => {
      const tierScheduleStepIndex = scheduleStepIndex
      scheduleStepIndex += 1
      if (tierScheduleStepIndex <= completedThroughStep) {
        return
      }

      const maxTierValue = line.category.tiers.at(-1)?.minInclusive ?? 1
      const metricRampMs = resolveRampingNumberDurationMs(
        0,
        line.metricValue,
        RUN_SUMMARY_METRIC_RAMP_MS,
        maxTierValue,
      )

      const revealAt = nextTierRevealAt
      nextTierRevealAt +=
        Math.max(
          TIER_REVEAL_MIN_STAGGER_MS,
          metricRampMs - TIER_REVEAL_OVERLAP_BEFORE_PREVIOUS_RAMP_END_MS,
        ) + (line.points > 0 ? TIER_REVEAL_STAGGER_EXTRA_WHEN_TAG_MS : 0)

      timers.push(
        window.setTimeout(() => {
          setRevealedTierCount(index + 1)
        }, revealAt),
      )

      const meterFillAt = revealAt + metricRampMs
      lastMeterFillAt = Math.max(lastMeterFillAt, meterFillAt)
      choreoEnd = Math.max(choreoEnd, meterFillAt)
      const tagIdealAt = meterFillAt + TIER_TAG_AFTER_METRIC_MS
      const tagScheduleAt = Math.max(tagIdealAt, nextTagSlotAt)
      if (line.points > 0) {
        const tagEnd = enqueueTierTagAt(
          tagScheduleAt,
          { kind: 'tier', index },
          line.points,
          `tier-${index}`,
        )
        nextTagSlotAt = tagEnd
        choreoEnd = Math.max(choreoEnd, tagEnd)
      }
    })

    choreoEnd = Math.max(choreoEnd, t)

    const badgeStartAt =
      badgeCount > 0 ? Math.max(lastMeterFillAt + BADGE_SEQUENCE_LEAD_MS, t) : choreoEnd
    const badgeSchedule = planBoggleBadgeRevealSchedule({
      badgeCount,
      startAt: badgeStartAt,
    })

    const badgesScheduleStepIndex = scheduleStepIndex
    if (badgeCount > 0) {
      scheduleStepIndex += 1
    }
    const badgesStepPending =
      badgeCount > 0 && badgesScheduleStepIndex > completedThroughStep
    if (badgesStepPending) {
      cumulativeBadgePoints = 0
    }

    badgeSchedule.forEach((step, index) => {
      if (!badgesStepPending) {
        return
      }
      const line = summaryScore.badgeLines[index]!
      /** Value to show on the pooled tag at this badge's spawn (timeouts must close over this, not the running sum). */
      const cumulativeForTagSpawn = cumulativeBadgePoints + line.points
      cumulativeScore += line.points
      cumulativeBadgePoints += line.points

      timers.push(
        window.setTimeout(() => {
          setBadgePhaseAt(index, 'revealing')
        }, step.revealAt),
      )

      timers.push(
        window.setTimeout(() => {
          setBadgeScoreTag({
            anchor: { kind: 'badge', index },
            cumulativeValue: cumulativeForTagSpawn,
            finalDrain: null,
            jiggleNonce: index,
          })
        }, step.tagSpawnAt),
      )

      timers.push(
        window.setTimeout(() => {
          setBadgePhaseAt(index, 'flying')
        }, step.flyStartAt),
      )

      timers.push(
        window.setTimeout(() => {
          setBadgePhaseAt(index, 'settled')
        }, step.settleAt),
      )
    })

    const scoreAfterBadges = cumulativeScore
    let scoringAnimationCompleteAt = choreoEnd
    /** When main Score reaches total after pooled badge drain; drives rating badge mount. */
    let ratingWhenScoreFullMs: number | null = null

    if (badgeCount > 0 && badgesStepPending) {
      const totalBadgePoints = summaryScore.badgeLines.reduce((s, l) => s + l.points, 0)
      const lastBadgeFlyStartAt = badgeSchedule[badgeSchedule.length - 1]!.flyStartAt
      const finalBadgeDrainMs = scaleRunResultsScoreRampDurationMs(
        totalBadgePoints,
        TAG_DRAIN_MS,
      )
      /** Pooled badge tag drain + Score ramp start exactly when list fly-in begins. */
      const badgePooledDrainStartsAt = lastBadgeFlyStartAt
      ratingWhenScoreFullMs = badgePooledDrainStartsAt + finalBadgeDrainMs

      timers.push(
        window.setTimeout(() => {
          setScore(scoreAfterBadges)
        }, badgePooledDrainStartsAt),
      )

      timers.push(
        window.setTimeout(() => {
          setBadgeScoreTag(prev => {
            if (!prev) {
              return null
            }
            return {
              ...prev,
              finalDrain: {
                durationMs: finalBadgeDrainMs,
                key: `${sequenceKey}:badge-final-drain`,
                /** Choreography already waits through per-badge spawn + hold beats. */
                preDrainDelayMs: BADGE_POOLED_TAG_PRE_DRAIN_DELAY_MS,
              },
            }
          })
        }, lastBadgeFlyStartAt),
      )

      scoringAnimationCompleteAt =
        badgePooledDrainStartsAt +
        finalBadgeDrainMs +
        BADGE_FLOATING_TAG_POST_DRAIN_HOLD_MS +
        TAG_FADE_OUT_MS
    }

    const ratingAt = ratingWhenScoreFullMs ?? scoringAnimationCompleteAt
    timers.push(
      window.setTimeout(() => {
        setShowRating(true)
        setResultsScoringAnimationsComplete(true)
      }, ratingAt),
    )

    const actionsReadyAt = ratingAt + RUN_SUMMARY_ACTIONS_REVEAL_DELAY_MS
    timers.push(
      window.setTimeout(() => {
        setSummaryActionsRevealReady(true)
      }, actionsReadyAt),
    )

    const outcomeAt = ratingAt + OUTCOME_DELAY_MS
    timers.push(
      window.setTimeout(() => {
        setShowOutcomeCopy(true)
      }, outcomeAt),
    )

    return () => {
      for (const timer of timers) {
        window.clearTimeout(timer)
      }
    }
  }, [
    choreographySteps,
    completedThroughStep,
    isActive,
    sequenceKey,
    skippedToSummaryEnd,
    tilesSeedPoints,
  ])

  useEffect(() => {
    if (!isActive || skippedToSummaryEnd) {
      setActiveAchievementEvent(null)
      return
    }

    if (!showOutcomeCopy || achievementEvents === null) {
      setActiveAchievementEvent(null)
      return
    }

    const events = achievementEvents ?? []
    if (events.length === 0) {
      setActiveAchievementEvent(null)
      return
    }

    const timers: number[] = []
    let t = RUN_SUMMARY_ACTIONS_REVEAL_DELAY_MS

    events.forEach((event, index) => {
      const showAt = t
      timers.push(
        window.setTimeout(() => {
          setActiveAchievementEvent(event)
        }, showAt),
      )
      t += RUN_SUMMARY_ACHIEVEMENT_TOAST_MS
      timers.push(
        window.setTimeout(() => {
          setActiveAchievementEvent(current => (current === event ? null : current))
        }, t),
      )
      t += index === events.length - 1 ? 0 : RUN_SUMMARY_ACHIEVEMENT_TOAST_GAP_MS
    })

    return () => {
      for (const timer of timers) {
        window.clearTimeout(timer)
      }
    }
  }, [achievementEvents, isActive, showOutcomeCopy, skippedToSummaryEnd])

  const clearBadgeFloatingTag = useCallback(() => setBadgeScoreTag(null), [])

  const skipSummaryScoringAnimations = useCallback(() => {
    setSkippedToSummaryEnd(true)
  }, [])

  return {
    activeAchievementEvent,
    badgeRevealPhases,
    badgeScoreTag,
    clearBadgeFloatingTag,
    goldDrain,
    resultsScoringAnimationsComplete,
    score,
    revealedTierCount,
    showOutcomeCopy,
    showRating,
    skippedToSummaryEnd,
    skipSummaryScoringAnimations,
    summaryActionsRevealReady,
    tierActiveTag,
  }
}

/**
 * Compatibility helper: the count of badges the surface should consider as
 * "ready to be displayed somewhere" — anything past `pending`.
 */
export function countRevealedBoggleBadges(
  phases: readonly BoggleBadgeRevealPhase[],
): number {
  return phases.filter(p => p !== 'pending').length
}

/** Re-export the per-step timing constants for tests + consumers. */
export { BADGE_REVEAL_TO_FLY_MS, BADGE_REVEAL_POP_MS, BADGE_TAG_PRE_DRAIN_DELAY_MS }
